import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuth, AuthState, User } from '../hooks/useAuth';
import { apiService } from '../services/api';

interface AuthContextType extends AuthState {
  logout: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  reauthenticate: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();

  // Efecto para sincronizar el token con el servicio API
  useEffect(() => {
    if (auth.token) {
      // Actualizar el token en el servicio API
      apiService.setAuthToken(auth.token);
      console.log('🔑 Token sincronizado con API service');
    } else {
      // Limpiar el token del servicio API
      apiService.setAuthToken(null);
      console.log('🧹 Token limpiado del API service');
    }
  }, [auth.token]);

  // Escuchar eventos de token expirado
  useEffect(() => {
    const handleTokenExpired = () => {
      console.warn('🔄 Token expirado detectado, reautenticando...');
      auth.reauthenticate();
    };

    window.addEventListener('auth-token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('auth-token-expired', handleTokenExpired);
    };
  }, [auth.reauthenticate]);

  const contextValue: AuthContextType = {
    ...auth,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

// Hook de conveniencia para obtener solo el usuario
export const useCurrentUser = (): User | null => {
  const { user } = useAuthContext();
  return user;
};

// Hook de conveniencia para verificar si está autenticado
export const useIsAuthenticated = (): boolean => {
  const { isAuthenticated } = useAuthContext();
  return isAuthenticated;
};
